import React, { useState } from 'react';
import { Link, useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import { LineChart, Loader2, Eye, EyeOff } from 'lucide-react';
import { BrandLogo } from '../../components/common/BrandLogo';
import { useAuth } from '../../features/auth/AuthContext';
import { formatApiErrorDetail } from '../../services/api/client';
import { REGISTER } from '../../constants/testIds';

export default function RegisterPage() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const rawNext = params.get('next') || '';
  const safeNext = rawNext.startsWith('/') ? rawNext : '';
  const loginHref = safeNext ? `/?next=${encodeURIComponent(safeNext)}` : '/';
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [registered, setRegistered] = useState(false);

  if (user) {
    return <Navigate to={user.roles.includes('admin') ? '/admin/dashboard' : '/member/dashboard'} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Password tidak cocok / Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await register({ full_name: fullName, email, phone, password });
      setRegistered(true);
      // Fire Lead event for Meta Pixel + TikTok Pixel + GTM (ad optimization signal).
      try {
        const w: any = window;
        if (typeof w.fbq === 'function') w.fbq('track', 'Lead', { content_name: 'Register', content_category: 'signup' });
        if (w.ttq && typeof w.ttq.track === 'function') w.ttq.track('CompleteRegistration', { content_name: 'Register' });
        if (Array.isArray(w.dataLayer)) w.dataLayer.push({ event: 'lead', method: 'register' });
      } catch { /* never break UX on tracking errors */ }
    } catch (err: any) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full bg-[#090D1A] border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder:text-white/30 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 outline-none transition-colors';

  return (
    <div className="min-h-screen bg-[#050914] flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute w-[800px] h-[800px] bg-cyan-500/10 blur-[120px] rounded-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="w-full max-w-md bg-white/[0.03] backdrop-blur-2xl border border-white/10 rounded-3xl p-8 sm:p-10 relative z-10 shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="mb-6">
          <BrandLogo />
        </div>

        <h1 className="text-3xl tracking-tight font-light text-white mb-2">Buat akun baru</h1>
        <p className="text-white/50 text-sm mb-6">Bergabung dengan komunitas trading.</p>

        {registered ? (
          <div data-testid="register-verify-notice" className="text-center py-4">
            <div className="w-14 h-14 rounded-2xl bg-emerald-400/10 border border-emerald-400/30 flex items-center justify-center mx-auto mb-4">
              <LineChart className="w-7 h-7 text-emerald-400" strokeWidth={1.5} />
            </div>
            <h2 className="text-lg font-medium text-white mb-2">Cek email Anda</h2>
            <p className="text-white/60 text-sm mb-6">Kami telah mengirim tautan verifikasi ke <span className="text-white">{email}</span>. Verifikasi dulu sebelum login.</p>
            <Link to={loginHref} className="inline-block bg-cyan-400 text-[#050914] font-medium px-6 py-2.5 rounded-lg hover:bg-cyan-300 transition-colors">Ke Halaman Login</Link>
          </div>
        ) : (

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
          <div>
            <label className="block text-sm text-white/70 mb-2">Nama Lengkap</label>
            <input type="text" required value={fullName} onChange={(e) => setFullName(e.target.value)} data-testid={REGISTER.nameInput} placeholder="Nama lengkap" className={inputClass} />
          </div>
          <div>
            <label className="block text-sm text-white/70 mb-2">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid={REGISTER.emailInput} placeholder="you@email.com" className={inputClass} />
          </div>
          <div>
            <label className="block text-sm text-white/70 mb-2">No. HP / Phone</label>
            <input type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="register-phone-input" placeholder="08xxxxxxxxxx" className={inputClass} />
          </div>
          <div>
            <label className="block text-sm text-white/70 mb-2">Password</label>
            <div className="relative">
              <input type={showPw ? 'text' : 'password'} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid={REGISTER.passwordInput} placeholder="Minimal 6 karakter" className={inputClass + ' pr-11'} />
              <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-cyan-400 transition-colors">
                {showPw ? <EyeOff className="w-4 h-4" strokeWidth={1.8} /> : <Eye className="w-4 h-4" strokeWidth={1.8} />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm text-white/70 mb-2">Konfirmasi Password</label>
            <input type={showPw ? 'text' : 'password'} required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid={REGISTER.passwordConfirmInput} placeholder="Ulangi password" className={inputClass} />
          </div>

          {error && (
            <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2.5" data-testid="register-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid={REGISTER.submitButton}
            className="w-full bg-cyan-400 text-[#050914] font-medium px-6 py-3 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />}
            Daftar / Register
          </button>
        </form>
        )}

        {!registered && (
        <p className="text-sm text-white/50 mt-6 text-center">
          Sudah punya akun?{' '}
          <Link to={loginHref} data-testid={REGISTER.loginLink} className="text-cyan-400 hover:text-cyan-300">
            Masuk di sini
          </Link>
        </p>
        )}
      </div>
    </div>
  );
}
